import type { CommodityCatalogProductsPageQuery } from 'types/graphql';

/**
 * ONE bounded page of catalog products. The commodity-first catalog asks for it scoped to a single
 * commodity (a `commodityId` grid filter) when a group expands or loads more, and walks it by cursor
 * for full-scope selection and export. `findManyPaginated` applies the organization predicate and
 * the soft-delete filter server-side.
 *
 * The five attribute relations are selected on EVERY row on purpose. The `Product` field
 * resolvers return a relation the row already carries instead of re-reading it, so a page of N
 * products costs one query rather than 5N + N.
 *
 * Search runs on the SERVER over the columns the grid used to fuzzy-match in the browser, which
 * is what lets it find a product nobody has loaded.
 */
export const COMMODITY_CATALOG_PRODUCTS_PAGE_QUERY = gql`
  query CommodityCatalogProductsPageQuery($input: StandardPageInput) {
    productPage(input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          disabled
          productCode
          gtinCode
          grade
          countryOfOrigin
          organic
          variableWeight
          weightPerInventoryUnit
          cartonsPerPallet
          costBasis
          customFields
          receivablesAccountId
          payablesAccountId
          incomeAccountId
          expenseAccountId
          commodity {
            id
            name
          }
          commodityStyle {
            id
            name
          }
          commoditySize {
            id
            name
          }
          commodityUnit {
            id
            name
          }
          commodityLabel {
            id
            name
          }
        }
      }
    }
  }
`;

type CatalogPageEdge = NonNullable<NonNullable<CommodityCatalogProductsPageQuery['productPage']['edges']>[number]>;

export type CommodityCatalogProduct = NonNullable<CatalogPageEdge['node']>;

export type CatalogProductRow = CommodityCatalogProduct & { group: string; description: string };

export type CatalogEmptyGroupRow = { id: string; group: string };

export type CatalogGridRow = {
  id: number | string;
  group?: string;
  name?: string;
  matchingProductCount?: number;
  description?: string;
  commodity?: { id: number; name: string } | null;
  commodityStyle?: { id: number; name: string } | null;
  commoditySize?: { id: number; name: string } | null;
  commodityUnit?: { id: number; name: string } | null;
  commodityLabel?: { id: number; name: string } | null;
  productCode?: string | null;
  gtinCode?: string | null;
  grade?: string | null;
  countryOfOrigin?: string | null;
  organic?: boolean | null;
  variableWeight?: boolean;
  disabled?: boolean;
  weightPerInventoryUnit?: number;
  cartonsPerPallet?: number | null;
  costBasis?: number | null;
  customFields?: unknown;
  receivablesAccountId?: number | null;
  payablesAccountId?: number | null;
  incomeAccountId?: number | null;
  expenseAccountId?: number | null;
};
